import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Input,
  Label,
  Badge,
} from '@machinemetrics/mm-react-components';
import {
  Plus,
  Building2,
  ArrowRight,
  Loader2,
  Factory,
  Gamepad2,
  Presentation,
  Trash2,
  RefreshCw,
  X,
} from 'lucide-react';
import { useMMUserContext } from '../hooks/useMMUser';

// API base URL
const API_BASE = '/api';

export default function CompanyLauncher() {
  const navigate = useNavigate();
  const { user, isRoot, canCreateCompany, loading: userLoading } = useMMUserContext();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyMode, setNewCompanyMode] = useState('GAME');
  const [creating, setCreating] = useState(false);

  // Fetch companies from the server (no mock fallback so you see real data or a clear error)
  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/companies`);
      if (!res.ok) throw new Error('Failed to fetch companies');
      const data = await res.json();
      setCompanies(data.companies ?? []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(err.message);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch when launcher is shown and when user is ready; refetch when returning to this page
  useEffect(() => {
    if (!userLoading) {
      fetchCompanies();
    }
  }, [userLoading]);

  // Refetch when user navigates back to the launcher so the list is fresh
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const isReturningToLauncher =
      prevPathRef.current !== '/launcher' && location.pathname === '/launcher';
    prevPathRef.current = location.pathname;
    if (isReturningToLauncher && !userLoading) {
      fetchCompanies();
    }
  }, [location.pathname, userLoading]);

  // Create new company (must succeed on server; no fake company so list stays in sync)
  const [createError, setCreateError] = useState(null);
  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return;

    setCreating(true);
    setCreateError(null);
    try {
      const res = await fetch(`${API_BASE}/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newCompanyName.trim(),
          mode: newCompanyMode,
        }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `Server error ${res.status}`);
      }

      const data = await res.json();
      const newCompany = data.company || data;
      setCompanies((prev) => [...prev, newCompany]);
      setCreateDialogOpen(false);
      setNewCompanyName('');

      navigate(`/company/${newCompany.id}/erp`);
    } catch (err) {
      console.error('Error creating company:', err);
      setCreateError(err.message || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  };

  // Delete company
  const handleDeleteCompany = async (companyId) => {
    if (!confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/companies/${companyId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete company');
      setCompanies(companies.filter((c) => c.id !== companyId));
    } catch (err) {
      // Surface the failure honestly — never optimistically remove on error, or the UI
      // lies and the company reappears on the next refetch (Postel — honest output).
      console.error('Error deleting company:', err);
      setError('Could not delete that company. Please try again.');
    }
  };

  if (userLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Factory className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-xl font-bold text-foreground">Manufacturing Hero</h1>
              <p className="text-sm text-muted-foreground">Company Launcher</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              Signed in as <span className="font-medium text-foreground">{user?.name}</span>
            </span>
            {isRoot && (
              <Badge variant="secondary">Root</Badge>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Your Companies</h2>
            <p className="text-muted-foreground">Select a company to manage or create a new one</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchCompanies} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
            {error}
          </div>
        )}

        {/* Companies Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Skeleton placeholders on first load so the grid isn't a blank void that reads
              as "no companies" while the fetch is in flight (Doherty Threshold). */}
          {loading && companies.length === 0 && [0, 1, 2].map((i) => (
            <div key={`sk-${i}`} className="bg-muted animate-pulse rounded-lg h-[220px]" />
          ))}

          {/* Existing Companies */}
          {companies.map((company) => (
            <Card key={company.id} className="group hover:border-primary transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{company.name}</CardTitle>
                      <CardDescription className="flex items-center gap-1 mt-1">
                        {company.mode === 'DEMO' ? (
                          <>
                            <Presentation className="w-3 h-3" />
                            Demo Mode
                          </>
                        ) : (
                          <>
                            <Gamepad2 className="w-3 h-3" />
                            Game Mode
                          </>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  {canCreateCompany && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCompany(company.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Created {new Date(company.createdAt).toLocaleDateString()}
                </p>
              </CardContent>
              <CardFooter>
                <Button
                  className="w-full"
                  onClick={() => navigate(`/company/${company.id}/floor`)}
                >
                  Enter
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}

          {/* Create New Company Card */}
          {canCreateCompany && (
            <>
              {/* First-run onboarding — a creator with zero companies should be welcomed
                  toward the single obvious next step, not shown a lone dashed card
                  (Peak-End / Von Restorff). */}
              {!loading && companies.length === 0 && (
                <div className="col-span-full text-center text-sm text-muted-foreground -mb-2">
                  Welcome! Create your first company to get started.
                </div>
              )}
              <Card
                className="border-dashed hover:border-primary cursor-pointer transition-colors group"
                onClick={() => { setCreateDialogOpen(true); setCreateError(null); }}
              >
                <CardContent className="flex flex-col items-center justify-center h-full min-h-[200px] p-8">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3 group-hover:bg-primary/10 transition-colors">
                    <Plus className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors font-medium">
                    Create New Company
                  </span>
                </CardContent>
              </Card>

              {/* Custom modal: fixed overlay, centered box, no overflow */}
              {createDialogOpen && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                  onClick={() => !creating && setCreateDialogOpen(false)}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="create-company-title"
                >
                  <div
                    className="bg-background border border-border rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex-none px-6 pt-6 pb-2 flex items-start justify-between gap-4">
                      <div>
                        <h2 id="create-company-title" className="text-lg font-semibold text-foreground">
                          Create New Company
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          Set up a new manufacturing company to manage
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => !creating && setCreateDialogOpen(false)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                        aria-label="Close"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="company-name">Company Name</Label>
                        <Input
                          id="company-name"
                          placeholder="ACME Manufacturing"
                          value={newCompanyName}
                          onChange={(e) => setNewCompanyName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Mode</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setNewCompanyMode('GAME')}
                            className={`p-4 rounded-lg border text-left transition-colors ${
                              newCompanyMode === 'GAME'
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-muted-foreground'
                            }`}
                          >
                            <Gamepad2 className={`w-5 h-5 mb-2 ${newCompanyMode === 'GAME' ? 'text-primary' : 'text-muted-foreground'}`} />
                            <div className="font-medium">Game Mode</div>
                            <div className="text-xs text-muted-foreground">
                              Full simulation with progression
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewCompanyMode('DEMO')}
                            className={`p-4 rounded-lg border text-left transition-colors ${
                              newCompanyMode === 'DEMO'
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-muted-foreground'
                            }`}
                          >
                            <Presentation className={`w-5 h-5 mb-2 ${newCompanyMode === 'DEMO' ? 'text-primary' : 'text-muted-foreground'}`} />
                            <div className="font-medium">Demo Mode</div>
                            <div className="text-xs text-muted-foreground">
                              Scripted scenarios for sales
                            </div>
                          </button>
                        </div>
                      </div>
                      {createError && (
                        <p className="text-sm text-destructive">
                          {createError}
                        </p>
                      )}
                    </div>
                    <div className="flex-none flex justify-end gap-2 px-6 py-4 border-t border-border">
                      <Button
                        variant="outline"
                        onClick={() => { setCreateDialogOpen(false); setCreateError(null); }}
                        disabled={creating}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreateCompany}
                        disabled={!newCompanyName.trim() || creating}
                      >
                        {creating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-2" />
                            Create Company
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {!loading && companies.length === 0 && !canCreateCompany && (
            <Card className="col-span-full">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Companies Available</h3>
                <p className="text-muted-foreground max-w-md">
                  You don&apos;t have access to any companies yet. 
                  Contact your administrator to get access.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Access */}
        <div className="mt-12 pt-8 border-t border-border">
          <h3 className="text-lg font-medium mb-4 text-muted-foreground">Quick Access</h3>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/machine-dashboard')}>
              Machine Dashboard
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/shop-floor')}>
              Shop Floor Editor
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/erp')}>
              ERP Dashboard
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
